import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../hooks/queries/useMe";
import { useUpdateProfile } from "../hooks/mutations/useUpdateProfile";
import Header from "../components/Header";
import toast from "react-hot-toast";
import step1Image from "../assets/step-1.webp";
import step2Image from "../assets/step-2.webp";
import step3Image from "../assets/step-3.webp";

const LinkKakaopayPage = () => {
  const navigate = useNavigate();
  const { data: user, isLoading } = useProfile();
  const { mutate: updateProfile, isPending } = useUpdateProfile();
  const [currentStep, setCurrentStep] = useState(1);
  const [kakaopayId, setKakaopayId] = useState("");
  const [isError, setIsError] = useState(false);
  const [initialValue, setInitialValue] = useState('');
  const [isInfo, setIsInfo] = useState(false);
  // 이미지 preload
  useEffect(() => {
    const preloadImages = () => {
      const images = [step1Image, step2Image, step3Image];
      images.forEach(imageSrc => {
        const img = new Image();
        img.src = imageSrc;
      });
    };
    
    preloadImages();
  }, []);

  // 사용자 정보가 로드되면 기본값 설정
  useEffect(() => {
    if (user?.kakaopay_id) {
      setKakaopayId(user.kakaopay_id);
      setInitialValue(user.kakaopay_id);
    }
  }, [user]);

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (!kakaopayId.trim() || isError) {
      toast.error('올바른 송금 ID를 입력해주세요');
      return;
    }

    updateProfile(
      { kakaopay_id: kakaopayId.trim() },
      {
        onSuccess: () => {
          toast.success('카카오페이 연동이 완료되었습니다!');
          navigate(-1);
        },
      }
    );
  };

  const renderTutorialStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="flex flex-col flex-1 justify-center gap-8">
            <div className="w-full mb-8">
              <label className="block">
                송금코드
              </label>
              <input
                type="text"
                name="kakaopay_id-search"
                autoComplete="off"
                value={kakaopayId}
                placeholder="복사한 코드 그대로 붙여넣기"
                className="w-full"
                disabled={isPending} 
                onPaste={(e) => {
                  e.preventDefault()
                  const pastedText = e.clipboardData.getData('text');
                  const parsedText = pastedText.split('/').pop() || '';
                  if(/^[A-Z][a-zA-Z0-9]{8}$/.test(parsedText)){
                    setKakaopayId(parsedText);
                    setIsError(false);
                    e.currentTarget.blur(); 
                  } else {
                    setIsError(true);
                  }
                }}
                onFocus={(e) => {
                  e.preventDefault()
                  e.currentTarget.select();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    e.preventDefault();
                    setKakaopayId('');
                    setIsError(false);
                    return;
                  }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                onInput={(e) => {
                  if(e.currentTarget.value.length === 1) {
                    e.preventDefault();
                  }
                }}
              />
              {isError && (
                <div className="text-red-500 mt-2 ">
                  정확한 송금코드를 붙혀넣어주세요!
                </div>
              )}

              <div className="mt-4 text-center">
                <button
                  className="text-gray-500 underline underline-offset-4"
                  onClick={() => setIsInfo((prev) => !prev)}
                >
                  송금코드를 등록하면 뭐가 좋나요?
                </button>
                {
                  isInfo && (
                    <div className="text-gray-800 text-center mt-4">
                      카카오페이 코드를 등록하면,
                      <br />
                      정산금을 카카오페이로 받을 수 있어요 !
                    </div>
                    )
                  }
              </div>
              <div className="mt-4 text-center">
                <button
                  className="text-gray-500 underline underline-offset-4"
                  onClick={() => setCurrentStep(1)}
                >
                  송금코드를 어떻게 확인하나요?
                </button>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md text-xl font-bold text-gray-800 mb-4">
              카카오톡 더보기 상단의
              <br />
              QR코드 아이콘을 눌러주세요
            </div>
            <div className="bg-gradient-to-b from-gray-50 to-white rounded-t-xl p-6 mb-4">
              <img src={step1Image} alt="step-1" className="w-full" />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md text-xl font-bold text-gray-800 mb-4">
              하단 세 개 버튼 중
              <br />
              송금코드 버튼을 눌러주세요.
            </div>
            <div className="bg-gradient-to-b from-gray-50 to-white rounded-t-xl p-6 mb-4">
              <img src={step2Image} alt="step-2" className="w-full" />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md text-xl font-bold text-gray-800 mb-4">
              QR코드 바로 아래
              <br />
              링크복사 버튼을 눌러주세요!
            </div>
            <div className="bg-gradient-to-b from-gray-50 to-white rounded-t-xl p-6 mb-4">
              <img src={step3Image} alt="step-3" className="w-full" />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 w-full pt-20 flex flex-col items-center">
        <Header title="카카오페이 연동" showBackButton={true} onBackClick={() => navigate(-1)} />
        <div className="w-8 h-8 mt-8 border-4 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 w-full pt-20 flex flex-col items-center">
      <Header title="카카오페이 연동" showBackButton={true} onBackClick={() => navigate(-1)} />
      
      <div className="w-full flex flex-col justify-between">
        {renderTutorialStep()}
        <div className="flex justify-center items-center gap-4">
          {[1,2,3].includes(currentStep) && (
            <button
              onClick={handlePrevious}
              className="flex-1 py-3.5 px-4 bg-gray-50 text-gray-400 text-lg font-semibold rounded-xl"
              disabled={isPending}
            >
              이전
            </button>
          )}
          <button
            type="submit"
            onClick={currentStep === 0 ? handleSubmit : handleNext}
            disabled={isPending || (currentStep === 0 && initialValue && kakaopayId.trim() === initialValue) || (currentStep === 0 && (!kakaopayId.trim() || isError))}
            className="flex-3 py-3.5 px-4 bg-blue-500 text-white text-lg font-semibold rounded-xl"
          >
            {isPending ? (
                '처리 중...'
              ) : currentStep === 0 ? (
                initialValue ? "수정하기" : "등록하기"
              ) : currentStep === 3 ? (
                "등록하러가기"
              ) : (
                "다음"
              )}
          </button>
        </div>
      </div>

    </div>
  );
};

export default LinkKakaopayPage;
